"use client";

import { useState } from "react";
import { toHumanCase } from "@huub-web/utils";

import type { Amenity } from "../types";

/**
 * Category item with id and human-readable label
 */
export type CategoryItem = {
  id: string;
  label: string;
};

/**
 * Props for the useAmenityCategoryFilter hook
 */
export type UseAmenityCategoryFilterProps = {
  /** Array of amenities to extract categories from */
  amenities: Amenity[];
};

/**
 * Return type for the useAmenityCategoryFilter hook
 */
export type UseAmenityCategoryFilterReturn = {
  /** List of unique categories with labels */
  categories: CategoryItem[];
  /** Currently selected category IDs */
  selectedCategories: string[];
  /** Toggle a category selection */
  handleCategoryToggle: (categoryId: string) => void;
  /** Get count of amenities in a category */
  getCategoryCount: (categoryId: string) => number;
};

/**
 * Hook for managing amenity category filtering.
 * Extracts unique categories from amenities and provides selection state.
 *
 * @param props - Configuration options
 * @returns Category data and filter handlers
 *
 * @example
 * ```tsx
 * const { categories, selectedCategories, handleCategoryToggle, getCategoryCount } =
 *   useAmenityCategoryFilter({ amenities });
 *
 * // Filter amenities
 * const filteredAmenities = amenities.filter(
 *   (a) => selectedCategories.length === 0 || selectedCategories.includes(a.category)
 * );
 * ```
 */
export const useAmenityCategoryFilter = ({
  amenities,
}: UseAmenityCategoryFilterProps): UseAmenityCategoryFilterReturn => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  /**
   * Extract unique categories from amenities and create labeled items
   */
  const categories: CategoryItem[] = (() => {
    const uniqueCategories = Array.from(
      new Set(
        amenities
          .map((amenity) => amenity.category)
          .filter((category): category is string => category !== undefined),
      ),
    );
    return uniqueCategories.sort().map((category) => ({
      id: category,
      label: toHumanCase(category),
    }));
  })();

  /**
   * Get the count of amenities in a specific category
   */
  const getCategoryCount = (categoryId: string) =>
    amenities.filter((amenity) => amenity.category === categoryId).length;

  /**
   * Toggle a category selection on/off
   */
  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((c) => c !== categoryId)
        : [...prev, categoryId],
    );
  };

  return {
    categories,
    selectedCategories,
    handleCategoryToggle,
    getCategoryCount,
  };
};
